import { memo } from 'react';
import type { ChipProps } from './types';

/**
 * Design tokens from Figma (node 70943-80982) – Input chip
 * - Height: 32px
 * - Background: var(--Schemes-Information-Container, #EAF3FF)
 * - Border radius: 8px
 * - Padding: 12px horizontal, 6px vertical
 * - Label content area: 91×20px; #1665C2, 14px, Roboto 500, line-height 20px, letter-spacing 0.1px
 * - Long text is truncated with visible ellipsis. Use title for native tooltip or wrap in Tooltip for show-on-truncation.
 */
const CHIP_LABEL_MAX_WIDTH_PX = 91;
const CHIP_LABEL_LINE_HEIGHT_PX = 20;

const CHIP_STYLES = {
  information: {
    container:
      'inline-flex items-center justify-center h-8 min-w-0 max-w-full overflow-hidden rounded-lg px-3 py-1.5',
    background: 'bg-[#EAF3FF]',
    label:
      'text-[#1665C2] text-sm font-medium leading-5 tracking-[0.1px] text-center block min-w-0 overflow-hidden text-ellipsis whitespace-nowrap',
    fontFamily: { fontFamily: 'Roboto, sans-serif' } as const,
  },
} as const;

function ChipComponent({
  children,
  variant = 'information',
  title,
  className = '',
}: ChipProps) {
  const style = CHIP_STYLES[variant];
  return (
    <span
      className={`${style.container} ${style.background} ${className}`}
      style={style.fontFamily}
      title={title}
    >
      <span
        className={style.label}
        style={{
          maxWidth: CHIP_LABEL_MAX_WIDTH_PX,
          lineHeight: CHIP_LABEL_LINE_HEIGHT_PX,
        }}
      >
        {children}
      </span>
    </span>
  );
}

export const Chip = memo(ChipComponent);
